import { getCookie } from 'cookies-next'
import { api } from './Axios'

export const PodCastAPI = {
    baseURL: '/wp/v2/podcast',

    baseParams: {
        order: 'desc',
        orderby: 'date',
        _embed: true, // Include embedded media, like featured image
    },

    // Fetch a single podcast by slug
    async getBySlug(slug) {
        try {
            const response = await api.get(`${this.baseURL}?slug=${slug}`, {
                params: this.baseParams,
            })
            return response.data.length > 0
                ? this._prepareSinglePodcast(response.data[0])
                : null
        } catch (error) {
            console.error(`Error fetching podcast with slug ${slug}:`, error)
            throw error
        }
    },

    // Fetch all podcasts in one shot using a loop (WordPress supports a maximum of 100 per page)
    async getAllInOneShot() {
        try {
            let allPodcasts = [] // Store all podcasts
            let page = 1 // Start with page 1
            let perPage = 100 // Maximum allowed per page is 100
            let totalPages = 1 // Default value to initiate the loop

            // Loop through all pages until we've fetched all podcasts
            while (page <= totalPages) {
                const { podcasts, totalPages: TotalsPages } = await this.getAll(
                    page,
                    perPage,
                )

                // Append fetched podcasts to the allPodcasts array
                allPodcasts = [...allPodcasts, ...podcasts]

                // Get the total number of pages from the response headers
                totalPages = TotalsPages

                // Increment the page to fetch the next set of results
                page += 1
            }

            return allPodcasts
        } catch (error) {
            console.error('Error fetching all podcasts in one shot:', error)
            throw error
        }
    },

    async getAllResultsBasedOnCustomParam(url, params = null) {
        try {
            let allPodcasts = [] // Store all podcasts
            let page = 1 // Start with page 1
            let perPage = 100 // Maximum allowed per page is 100
            let totalPages = 1 // Default value to initiate the loop

            // Loop through all pages until we've fetched all podcasts
            while (page <= totalPages) {
                const { podcasts, totalPages: TotalsPages } = await this.getAll(
                    page,
                    perPage,
                    url,
                    params,
                )

                // Append fetched podcasts to the allPodcasts array
                allPodcasts = [...allPodcasts, ...podcasts]

                // Get the total number of pages from the response headers
                totalPages = TotalsPages

                // Increment the page to fetch the next set of results
                page += 1
            }

            return allPodcasts
        } catch (error) {
            console.error('Error fetching all podcasts in one shot:', error)
            throw error
        }
    },

    // Fetch all podcasts with pagination support and embed featured media
    async getAll(page = 1, perPage = 10, url, params) {
        const fetchURL = url ? url : this.baseURL
        const fetchParams = params
            ? params
            : {
                  order: 'desc',
                  orderby: 'date',
                  page: page,
                  per_page: perPage,
                  _embed: true, // Include embedded media, like featured image
              }

        try {
            const response = await api.get(fetchURL, { params: fetchParams })

            const totalPages = response.headers['x-wp-totalpages'] // Get total number of pages
            const totalPosts = response.headers['x-wp-total'] // Get total number of posts

            // Process podcasts and include featured image URL
            const podcasts = response.data.map((podcast) => {
                return this._prepareSinglePodcast(podcast)
            })

            return {
                podcasts,
                totalPages,
                totalPosts,
            }
        } catch (error) {
            console.error('Error fetching podcasts with pagination:', error)
            throw error
        }
    },

    // fetch categoreis usin /wp-json/wp/v2/podcast_category
    async getCategories() {
        try {
            const response = await api.get('wp/v2/podcast_category')
            const categories = await Promise.all(
                response.data.map(async (category) => {
                    const totalPosts = await this.getPodcastsByCategory(
                        category.id,
                    )
                    return {
                        ...category,
                        totalPosts: totalPosts.length,
                    }
                }),
            )
            return categories
        } catch (error) {
            console.error('Error fetching all podcasts:', error)
            throw error
        }
    },

    // Fetch category by slug
    async getCategoryBySlug(slug) {
        try {
            const response = await api.get(
                `wp/v2/podcast_category?slug=${slug}`,
            )
            return response.data.length > 0 ? response.data[0] : null
        } catch (error) {
            console.error(`Error fetching category with slug ${slug}:`, error)
            throw error
        }
    },

    // Fetch podcasts by category ID
    async getPodcastsByCategory(categoryId) {
        try {
            const response = await this.getAllResultsBasedOnCustomParam(
                `wp/v2/podcast?podcast_category=${categoryId}`,
                this.baseParams,
            )

            return response ?? []
        } catch (error) {
            console.error(
                `Error fetching podcasts for category ${categoryId}:`,
                error,
            )
            throw error
        }
    },

    // fetch tags using /wp-json/wp/v2/podcast_tag
    async getTags() {
        try {
            const response = await api.get('wp/v2/podcast_tag')
            return response.data
        } catch (error) {
            console.error('Error fetching all podcasts:', error)
            throw error
        }
    },

    // Fetch tag by slug
    async getTagBySlug(slug) {
        try {
            const response = await api.get(`wp/v2/podcast_tag?slug=${slug}`)
            return response.data.length > 0 ? response.data[0] : null
        } catch (error) {
            console.error(`Error fetching tag with slug ${slug}:`, error)
            throw error
        }
    },

    // Fetch podcasts by tag ID
    async getPodcastsByTag(tagId) {
        try {
            const response = await this.getAllResultsBasedOnCustomParam(
                `wp/v2/podcast?podcast_tag=${tagId}`,
            )
            return response || []
        } catch (error) {
            console.error(`Error fetching podcasts for tag ${tagId}:`, error)
            throw error
        }
    },

    // Fetch related posts based on categories
    async getRelatedPosts(podcastId, categories) {
        try {
            let relatedPosts = []

            for (const categoryId of categories) {
                const postsInCategory = await this.getPodcastsByCategory(
                    categoryId,
                )

                // Filter out the original podcast to avoid duplication
                let filteredPosts = postsInCategory.filter(
                    (podcast) => podcast.id !== podcastId,
                )

                filteredPosts = filteredPosts.map((podcast) =>
                    this._prepareSinglePodcast(podcast),
                )

                // Add filtered related posts to the array
                relatedPosts = [...relatedPosts, ...filteredPosts]
            }

            // Return unique related posts
            return [...new Set(relatedPosts)]
        } catch (error) {
            console.error(
                `Error fetching related posts for podcast ${podcastId}:`,
                error,
            )
            throw error
        }
    },

    _prepareSinglePodcast(podcast) {
        // Extract the featured image URL from the embedded media
        const image_url =
            podcast?._embedded && podcast?._embedded['wp:featuredmedia']
                ? podcast._embedded['wp:featuredmedia'][0].source_url
                : null

        return {
            ...podcast,
            thumbnail: { url: image_url }, // Add featured image URL to the podcast object
        }
    },

    // Post a comment to a podcast/post
    async postComment({ postId, name, email, comment }) {
        const baseURL = '/wp/v2/comments'

        // Retrieve JWT token from the 'olm-jwt' cookie
        const jwtToken = getCookie('olm-jwt')

        try {
            const response = await api.post(
                baseURL,
                {
                    post: postId, // The ID of the post to comment on
                    author_name: name, // Comment author name
                    author_email: email, // Comment author email
                    content: comment, // The content of the comment
                },
                {
                    headers: {
                        Authorization: `Bearer ${jwtToken}`, // Include the JWT token
                    },
                },
            )

            return response.data
        } catch (error) {
            console.error(`Error posting comment for post ${postId}:`, error)
            throw error
        }
    },

    // Fetch comments by post ID
    async getCommentsByPostId(postId) {
        const baseURL = '/wp/v2/comments'
        try {
            const response = await api.get(`${baseURL}?post=${postId}`)
            return response.data
        } catch (error) {
            console.error(`Error fetching comments for post ${postId}:`, error)
            throw error
        }
    },
}
